import { Link } from 'react-router-dom';

import { useAgentRefs } from '@/app/agentConversation/_context';
import { AGENT_KINDS } from '@/app/agentConversation/_enums';
import { pathForRef, refKey } from '@/app/agentConversation/_helpers';
import { formatCompactCurrency } from '@/app/currency/_helpers';

import { Badge } from '@/app/ui/badge';
import { useCurrencyObjectByCode } from '@/data/currency';

/**
 * A record in a `cards` block: its name, where it sits, its status and, for an
 * estimate, its total. A record with no summary — deleted, or not visible to
 * this user — shows as unavailable rather than inventing a label. Mounted
 * under ShowCards, which has loaded the currency list.
 */
const RecordCard = ({ refValue }) => {
  const refs = useAgentRefs();
  const kind = AGENT_KINDS[refValue.kind];
  const summary = refs[refKey(refValue)];
  const Icon = kind.icon;

  if (!summary) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm" data-testid={`agent-card-${refValue.uid}`}>
        <Icon className="size-4" />
        {kind.label} not available
      </div>
    );
  }

  return (
    <Link
      to={pathForRef(refValue)}
      data-testid={`agent-card-${refValue.uid}`}
      className="bg-card hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 transition-colors"
    >
      <div className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-md">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{summary.title}</div>
        {summary.subtitle && <div className="text-muted-foreground truncate text-xs">{summary.subtitle}</div>}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge variant="outline">{kind.label}</Badge>
          {summary.status && (
            <Badge variant="outline">
              <span className="bg-muted-foreground size-1.5 shrink-0 rounded-full" />
              {summary.status}
            </Badge>
          )}
        </div>
      </div>
      {summary.total_cost !== undefined && summary.total_cost !== null && summary.currency && <RecordCardTotal value={summary.total_cost} code={summary.currency} />}
    </Link>
  );
};

// Money without a currency would be a bare number posing as one, so a total
// whose currency the list does not hold is left off rather than guessed.
const RecordCardTotal = ({ value, code }) => {
  const currency = useCurrencyObjectByCode(code);
  if (!currency) return null;

  return <div className="shrink-0 text-right text-sm font-medium tabular-nums">{formatCompactCurrency(value, currency)}</div>;
};

export default RecordCard;
